/**
 * Committed Costs Service (mocked, API-ready)
 *
 * Exposes third-party costs that are already contracted/mapped but not yet
 * invoiced or posted to the DRE. These eventually land as expenses against
 * Lucro Líquido, so the CEO Big Numbers card uses them to compute
 * "Lucro Ajustado".
 *
 * IMPORTANT: This file is intentionally shaped like a tRPC hook consumer so
 * the future migration is a single-file swap. When the backend endpoint is
 * ready, replace the mock implementation below with something like:
 *
 *     const { data, isLoading } = trpc.committedCosts.list.useQuery()
 *     return {
 *       value: data?.total ?? 0,
 *       breakdown: data?.items,
 *       isLoading,
 *     }
 *
 * Consumers (`CEOBigNumbersDashboard`, etc.) must NOT import the MOCK_*
 * constants directly — they only depend on the hook's return shape.
 */

export interface CommittedTerceirosBreakdownItem {
  label: string
  value: number
  client?: string
  pit?: string
}

export interface CommittedTerceirosResult {
  /** Total committed-but-unposted third-party costs in R$ */
  value: number
  /** Optional itemized breakdown (reserved for future tooltip) */
  breakdown?: CommittedTerceirosBreakdownItem[]
  isLoading: boolean
}

// TODO(api): Replace these mocks with the real tRPC query output when the
// backend endpoint is ready. Keep the hook return shape identical so the
// dashboard does not need to change.
const MOCK_TOTAL = 2_400_000

const MOCK_BREAKDOWN: CommittedTerceirosBreakdownItem[] = [
  { label: 'Fornecedor A', value: 1_200_000, client: 'Cliente X', pit: 'PIT-2026-001' },
  { label: 'Fornecedor B', value: 800_000, client: 'Cliente Y', pit: 'PIT-2026-002' },
  { label: 'Fornecedor C', value: 400_000, client: 'Cliente Z', pit: 'PIT-2026-003' },
]

/**
 * Returns the total value of third-party costs already committed but not
 * yet posted to the DRE.
 */
export function useCommittedTerceiros(): CommittedTerceirosResult {
  return {
    value: MOCK_TOTAL,
    breakdown: MOCK_BREAKDOWN,
    isLoading: false,
  }
}
